import { i18nTranslationHelper } from '@/i18n'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnalyticsTracker, AnalyticsEventName } from '@/infra/tracking'
import { AllSearchTypeOption, OptionsType } from './search-type-decisor'
import { FeatureFlagMetricName, FeatureFlagTracker } from '@/infra/feature-flag'

export const MAX_AUTOCOMPLETE_ITEMS = 7
export const MIN_AUTOCOMPLETE_SEARCH_LENGTH = 3

type MatchedWord = {
  term: string
  match: { startIndex: number; endIndex: number }
}

const importTermsDynamically = (language: string) => {
  switch (language) {
    case 'es':
      return import('./terms-es.json')
    case 'en':
      return import('./terms-en.json')
    case 'pt':
    default:
      return import('./terms-pt.json')
  }
}

export const useGlobalSearch = () => {
  const [displayAutocomplete, setDisplayAutocomplete] = useState(false)
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false)
  const [matchedWords, setMatchedWords] = useState<MatchedWord[]>([])
  const [language] = useState<string>(i18nTranslationHelper.getCurrentLanguage())
  const [terms, setTerms] = useState<string[]>([])
  const [searchType, setSearchType] = useState(AllSearchTypeOption)

  const navigate = useNavigate()
  const location = useLocation()

  const getSearchTermFromParams = () => {
    const params = new URLSearchParams(location.search)
    return params.get('q') || params.get('name') || ''
  }

  const [search, setSearch] = useState<string>(getSearchTermFromParams())

  useEffect(() => {
    importTermsDynamically(language).then(module => {
      setTerms(module.default)
    })
  }, [language])

  useEffect(() => {
    const term = getSearchTermFromParams()
    if (term !== search) {
      setSearch(term)
    }
  }, [location.search, location.pathname])

  const handleSearch = (value: string | undefined, searchType: OptionsType) => {
    const valueTrimmed = (value ?? '').trim()
    setSearch(valueTrimmed)

    if (!searchType.value) {
      return
    }

    AnalyticsTracker.trackEvent(AnalyticsEventName.SEARCH_BAR_SEARCH, {
      term: valueTrimmed,
      to: searchType.value,
      source: location.pathname
    })

    FeatureFlagTracker.trackEvent(FeatureFlagMetricName.SEARCH_GLOBAL, {
      term: valueTrimmed,
      to: searchType.value,
      source: location.pathname
    })

    navigate({
      pathname: searchType.value,
      search: `${searchType.paramName}=${encodeURIComponent(valueTrimmed)}`
    })

    setMatchedWords([])
    setDisplayAutocomplete(false)
  }

  const autoComplete = (value: string | undefined) => {
    const valueTrimmed = (value ?? '').trim()
    setSearch(valueTrimmed)

    if (!valueTrimmed) {
      setSearch('')
      setMatchedWords([])
      if (displayAutocomplete) setDisplayAutocomplete(false)
      return
    }

    if (valueTrimmed.length < MIN_AUTOCOMPLETE_SEARCH_LENGTH) {
      return
    }

    setMatchedWords([])
    setLoadingAutocomplete(true)
    setDisplayAutocomplete(true)

    const matchedWords = terms
      .map(term => {
        const matchIndex = term.toLowerCase().indexOf(value!.toLowerCase())
        if (matchIndex !== -1) {
          return {
            term: term,
            match: {
              startIndex: matchIndex,
              endIndex: matchIndex + value!.length - 1
            }
          }
        }
        return null
      })
      .filter(term => term !== null)
      .slice(0, MAX_AUTOCOMPLETE_ITEMS) as MatchedWord[]

    setTimeout(() => {
      setLoadingAutocomplete(false)
      setMatchedWords(matchedWords)
    }, 300)
  }

  const handleAutoCompleteSelect = (term: string) => {
    setSearch(term)
    handleSearch(term, searchType)
  }

  const handleSearchTypeChange = (option: OptionsType) => {
    setSearchType(option)
    handleSearch(search, option)
  }

  const handleSearchTypeUpdate = (option: OptionsType) => {
    setSearchType(option)
  }

  const handleSearchInputFocus = () => {
    if (search.length < 1) {
      setDisplayAutocomplete(false)
      return
    }

    setDisplayAutocomplete(false)

    if (search.length >= MIN_AUTOCOMPLETE_SEARCH_LENGTH) {
      autoComplete(search)
    }
  }

  const handleClearInput = () => {
    setSearch('')
    setMatchedWords([])
  }

  return {
    search,
    searchType,
    displayAutocomplete,
    loadingAutocomplete,
    matchedWords,
    handleSearch,
    handleAutoCompleteSelect,
    handleSearchTypeChange,
    handleSearchTypeUpdate,
    handleSearchInputFocus,
    handleClearInput,
    autoComplete,
    setDisplayAutocomplete
  }
}
